import L from 'leaflet';
import type { Root } from 'react-dom/client';
import * as turf from '@turf/turf';

export interface RotateControlState {
  marker: L.Marker;
  root: Root;
  container: HTMLDivElement;
  mounted: boolean;
  cleanup: () => void;
}

export interface RotateControlMetrics {
  shellSize: number;
  iconSize: number;
  offset: number;
}

export interface RotatePolygonOptions {
  pivot?: [number, number];
}

export const getRotateControlMetrics = (map: L.Map): RotateControlMetrics => {
  const zoom = map.getZoom();
  const normalizedZoom = Math.min(Math.max((zoom - 18) / 8, 0), 1);

  return {
    shellSize: 32,
    iconSize: 14,
    offset: Math.round(18 + normalizedZoom * 14),
  };
};

export const syncRotateControlAppearance = (map: L.Map, marker: L.Marker, container?: HTMLDivElement) => {
  const { shellSize, iconSize } = getRotateControlMetrics(map);
  if (container) {
    container.style.setProperty('--rotate-control-shell-size', `${shellSize}px`);
    container.style.setProperty('--rotate-control-icon-size', `${iconSize}px`);
  }

  marker.setIcon(
    L.divIcon({
      className: 'grid-rotate-control-marker',
      html: container ?? '',
      iconSize: [shellSize, shellSize],
      iconAnchor: [Math.round(shellSize / 2), Math.round(shellSize / 2)],
    })
  );
};

export const getRotateControlLatLng = (layerRef: L.Polygon, map: L.Map) => {
  const latLngs = layerRef.getLatLngs();
  const outerRing = (Array.isArray(latLngs?.[0]) ? latLngs[0] : latLngs) as L.LatLng[] | undefined;
  if (!outerRing?.length) {
    return null;
  }

  const { offset } = getRotateControlMetrics(map);
  const vertexPoints = outerRing.map((latLng) => map.latLngToContainerPoint(latLng));
  const topVertexPoint = vertexPoints.reduce((selectedPoint, currentPoint) => {
    if (currentPoint.y < selectedPoint.y) {
      return currentPoint;
    }
    if (currentPoint.y === selectedPoint.y && currentPoint.x > selectedPoint.x) {
      return currentPoint;
    }
    return selectedPoint;
  });

  const centroidPoint = vertexPoints.reduce(
    (accumulator, currentPoint) => L.point(accumulator.x + currentPoint.x, accumulator.y + currentPoint.y),
    L.point(0, 0)
  );
  const normalizedCentroidPoint = L.point(centroidPoint.x / vertexPoints.length, centroidPoint.y / vertexPoints.length);
  const directionVector = L.point(
    topVertexPoint.x - normalizedCentroidPoint.x,
    topVertexPoint.y - normalizedCentroidPoint.y
  );
  const directionLength = Math.hypot(directionVector.x, directionVector.y);
  const normalizedDirection =
    directionLength > 0
      ? L.point(directionVector.x / directionLength, directionVector.y / directionLength)
      : L.point(0.6, -0.8);
  const offsetPoint = L.point(
    topVertexPoint.x + normalizedDirection.x * offset,
    topVertexPoint.y + normalizedDirection.y * offset
  );

  return map.containerPointToLatLng(offsetPoint);
};

export const refreshEditHandles = (layerRef: L.Polygon) => {
  if (!layerRef.pm?.enabled?.()) {
    return;
  }

  const pmInstance = layerRef.pm as typeof layerRef.pm & { options?: Record<string, unknown> };
  const currentOptions = pmInstance.options ? { ...pmInstance.options } : undefined;
  layerRef.pm.disable();
  layerRef.pm.enable(currentOptions);
};

export const rotatePolygonOuterRing = (
  latLngs: L.LatLng[],
  angleDegrees: number,
  options: RotatePolygonOptions = {}
): L.LatLng[] | null => {
  if (!latLngs.length) {
    return null;
  }

  const polygon = turf.polygon([[...latLngs.map(({ lng, lat }) => [lng, lat]), [latLngs[0].lng, latLngs[0].lat]]]);
  const pivot = options.pivot ?? turf.center(polygon).geometry.coordinates;
  const rotatedPolygon = turf.transformRotate(polygon, angleDegrees, { pivot });
  const rotatedRing = rotatedPolygon.geometry.coordinates[0]?.slice(0, -1) ?? [];
  if (rotatedRing.length < 3) {
    return null;
  }

  return rotatedRing.map(([lng, lat]) => L.latLng(lat, lng));
};
